import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom, map, mergeMap, combineLatest, startWith, of, catchError, tap, shareReplay } from 'rxjs';
import { Bundle, ChargeItem, CodeableConcept, DiagnosticReport, ImagingStudy, Invoice, Observation, Procedure, ServiceRequest } from 'fhir/r4';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { DetailzViewzComponent } from '../detailz-viewz/detailz-viewz.component';
import { DetailsBuilderObject } from '../detailz-viewz/details-builder.service';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { NaPipe } from '../shared/na.pipe';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';
import { StateService } from '../shared/state.service';
import { UtilityService } from '../shared/utility.service';
import { ChipsDirective } from '../chips.directive';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { AuthService } from '../shared/auth/auth.service';
import { AddServiceRequestComponent } from './add-service-request/add-service-request.component';
import { AdmittedPatientsComponent } from '../admitted-patients/admitted-patients.component';
import { ServiceRequestsService } from './service-requests.service';
import { PatientAddressPipe } from '../shared/pipes/patient-address.pipe';
import { PatientAgePipe } from '../shared/pipes/patient-age.pipe';
import { PatientContactsPipe } from '../shared/pipes/patient-contacts.pipe';
import { PatientNamePipe } from '../shared/pipes/patient-name.pipe';
import { CodeableConcept2Pipe } from "../shared/codeable-concept2.pipe";
import { EncounterServiceService } from '../patient-wrapper/encounter-service.service';
import { DetailActionButton } from '../detailz-viewz/detailz-viewz.component';
import { RequestPrintService } from '../shared/request-print.service';
import { AddReportComponent } from '../labreport/add-report/add-report.component';
import { referenceArrayContains, referenceMatches } from '../shared/fhir-reference.util';
import { LabBillingPlaceholderService } from '../shared/services/lab-billing-placeholder.service';
import { ServiceRequestBillingPlaceholderService } from '../shared/services/service-request-billing-placeholder.service';
import { AddImagingStudyComponent } from './add-imaging-study/add-imaging-study.component';
import { RequiredObservationResultsTableComponent } from '../shared/required-observation-results-table/required-observation-results-table.component';
import { ItemsRequiredEditorComponent } from '../shared/items-required-editor/items-required-editor.component';
import { Observable } from 'rxjs';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { switchMap } from 'rxjs/operators';

import { BehaviorSubject } from 'rxjs';
import { ResourceStatusChangesService } from '../resource-status-changes/resource-status-changes.service';
import { inferServiceRequestType } from '../shared/service-request-type.util';
import { ServiceRequestSearchService } from './service-request-search.service';
import { CustomTabsComponent, TabItem } from '../shared/custom-tabs/custom-tabs.component';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { ResourceRefreshService } from '../shared/resource-refresh.service';
import { ProcedureReportDialogComponent } from '../procedure-report/procedure-report-dialog.component';
import { ProcedureReportViewDialogComponent } from '../procedure-report/procedure-report-view-dialog.component';
import { ReferenceIdentifierPipe } from '../shared/reference-identifier.pipe';
import { ProcedureFinalizationInventoryService } from '../shared/services/procedure-finalization-inventory.service';
import { UnifiedNotificationService } from '../shared/unified-notification.service';
import { NotificationService } from '../shared/notification/notification.service';
import { AddSpecimenComponent } from '../specimen/add-specimen/add-specimen.component';
import { CompleteRequestChoiceDialogComponent, CompleteRequestChoice } from '../shared/complete-request-choice-dialog/complete-request-choice-dialog.component';


@Component({
    selector: 'app-service-requests',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatDividerModule,
        MatSnackBarModule,
        DatePipe,
        MatTableModule,
        ReferenceDisplayDirective,
        EmptyStateComponent,
        NaPipe,
        ChipsDirective,
        TableHeaderComponent,
        CodeableConcept2Pipe,
        CustomTabsComponent,
        MatPaginatorModule,
        ReferenceIdentifierPipe
    ],
    providers: [PatientNamePipe, PatientContactsPipe, PatientAgePipe, PatientAddressPipe,
    ],
    templateUrl: './service-requests.component.html',
    styleUrls: ['../medicine-requests/medicine-requests.component.scss', './service-requests.component.scss']
})
export class ServiceRequestsComponent extends AdmittedPatientsComponent {
    private route = inject(ActivatedRoute);
    private utilityService = inject(UtilityService);
    private serviceRequestIdFromRoute: string | null = null;
    private autoDialogOpened = false;

    allowedServiceRequestTypes$: Observable<string[]> = of([]);
    searchTerm$ = new BehaviorSubject<string>('');
    activeFilters$ = new BehaviorSubject<{ [key: string]: string[] }>({});
    fullDataSet: any[] = [];
    private invoiceCacheMap = new Map<string, Promise<any>>();

    categoryFiltering = new FormControl('all');
    categoryTabs: TabItem[] = [
        { label: 'All', id: 'all' },
        { label: 'Imaging', id: 'imaging' },
        { label: 'Procedure', id: 'procedure' },
        { label: 'Surgery', id: 'surgery' },
        { label: 'Observation', id: 'observation' },
        { label: 'Others', id: 'others' }
    ];

    searchableFields = [
        { label: 'Patient ID', value: 'subject.identifier' },
        { label: 'Service Name', value: 'code:text' }
    ];
    selectedSearchField = 'subject.identifier';

    pageSize = 25;
    paginatorPageIndex = 0;

    private resourceRefresh = inject(ResourceRefreshService);
    srSearch = inject(ServiceRequestSearchService);

    loading$ = this.srSearch?.loading$;
    total$ = this.srSearch?.displayedTotal$;
    error$ = this.srSearch?.error$;

    subToService: any = null;

    // dummy openadddialog
    openAddDialog(): void {
        if (!this.auth.can('serviceRequest', 'add')) {
            this.errorService.openandCloseError('You do not have permission to add service requests.');
            return;
        }

        const type = (this.route.snapshot.paramMap.get('type') || '')
            .toString()
            .trim()
            .toLowerCase();

        // Check if user can access this service request type
        if (type && type !== 'others' && !this.auth.canAccessServiceRequestCategory(type)) {
            this.errorService.openandCloseError(
                `You do not have permission to create ${type} service requests.`
            );
            return;
        }

        const typeOfService = type && type !== 'others' ? type : null;

        this.encounterService.addServiceRequest(
            this.patientId as string,
            typeOfService,
            false,
            true
        );
    }

    encounterService = inject(EncounterServiceService);
    private requestPrint = inject(RequestPrintService);
    private snackBar = inject(MatSnackBar);
    private serviceRequestPaymentResolver = inject(ServiceRequestBillingPlaceholderService);
    private statusChanges = inject(ResourceStatusChangesService);
    private finalizationInventory = inject(ProcedureFinalizationInventoryService);
    private unifiedNotificationService = inject(UnifiedNotificationService);
    private notifications = inject(NotificationService);

    override displayedColumns = ['requestId', 'authoredOn', 'status', 'category', 'code', 'subject', 'action'];

    // Add must be doctor-only (capacity object controls this)
    override canAdd$ = this.auth.user.pipe(map(() => this.auth.can('serviceRequest', 'add')));

    detailsBuilder: DetailsBuilderObject = {
        resourceName: 'ServiceRequest',
        resourceIcon: 'assignment',
        specialHeader: {
            strongSectionKey: 'code',
            iconSectionKeys: ['status', 'available'],
            contentSectionKeys: ['category', 'subject', 'authoredOn']
        },
        groups: [
            { groupName: 'Classification', groupIcon: 'category', groupKeys: ['Resource_Identifier', 'status', 'intent', 'priority', 'performerType'] },
            { groupName: 'Participants', groupIcon: 'group', groupKeys: ['subject', 'requester', 'performer'] },
            { groupName: 'Clinical Details', groupIcon: 'science', groupKeys: ['code', 'reasonCode', 'flattenedNote'] },
            { groupName: 'Logistics', groupIcon: 'calendar_today', groupKeys: ['authoredOn', 'requisition', 'systemReference'] }
        ],
        otherKeys: ['available']
    };

    useOrgWide = true;
    patientId: string | null = null;

    private readonly terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);
    private readonly organizationTenantTagSystem = `${this.terminologyBaseUrl}/CodeSystem/organizationTenant`;

    private readonly availabilitySets$ = this.utilityService.getAllServiceRequestCodesFromCodeSystem().pipe(
        map((options: any[]) => {
            const pairSet = new Set<string>();
            const codeSet = new Set<string>();
            const displaySet = new Set<string>();
            for (const opt of (options || [])) {
                if (!opt?.active) continue;
                const code = (opt?.code || '').toString().trim().toLowerCase();
                if (!code) continue;
                codeSet.add(code);
                const system = (opt?.system || '').toString().trim().toLowerCase();
                if (system) pairSet.add(`${system}::${code}`);
                const display = (opt?.display || '').toString().trim().toLowerCase();
                if (display) displaySet.add(display);
            }
            return { pairSet, codeSet, displaySet };
        }),
        shareReplay(1),
        catchError(() => of({ pairSet: new Set<string>(), codeSet: new Set<string>(), displaySet: new Set<string>() }))
    );

    private normalizeIdFromReference(ref: any): string | null {
        const s = (ref ?? '').toString().trim();
        if (!s) return null;

        // Accept: 'Organization/{id}', '.../Organization/{id}', or bare '{id}'
        const orgIdx = s.lastIndexOf('Organization/');
        if (orgIdx >= 0) {
            const tail = s.substring(orgIdx + 'Organization/'.length);
            const clean = tail.split(/[?#/]/)[0];
            return clean || null;
        }
        const clean = s.split(/[?#/]/).pop();
        return clean || null;
    }

    private extractTenantOrgIdFromServiceRequest(sr: ServiceRequest | any): string | null {
        if (!sr) return null;

        const tags: any[] = Array.isArray(sr?.meta?.tag) ? sr.meta.tag : [];
        const tenantTag = tags.find((coding: any) => this.isOrganizationTenantSystem(coding?.system));
        const tenantCode = tenantTag?.code ?? tenantTag?.value ?? null;
        const tenantIdFromTag = this.normalizeIdFromReference(tenantCode);
        if (tenantIdFromTag) {
            return tenantIdFromTag;
        }

        // Backwards compatibility for legacy extension-based tagging
        const exts: any[] = Array.isArray(sr?.extension) ? sr.extension : [];
        const tenantExt = exts.find((e: any) => this.isOrganizationTenantExtension(e?.url));
        const tenantRef = tenantExt?.valueReference?.reference ?? null;
        const tenantId = this.normalizeIdFromReference(tenantRef);
        if (tenantId) {
            return tenantId;
        }

        const performers: any[] = Array.isArray(sr?.performer) ? sr.performer : [];
        const orgPerformer = performers
            .map((p: any) => p?.reference ?? p)
            .map((r: any) => this.normalizeIdFromReference(r))
            .find((id: any) => !!id);
        return orgPerformer ?? null;
    }

    private isOrganizationTenantSystem(system: string | null | undefined): boolean {
        const normalized = (system ?? '').toString();
        if (!normalized) return false;
        return normalized === this.organizationTenantTagSystem || normalized.endsWith('/CodeSystem/organizationTenant');
    }

    private isOrganizationTenantExtension(url: string | null | undefined): boolean {
        const normalized = (url ?? '').toString();
        if (!normalized) return false;
        return normalized.endsWith('/StructureDefinition/organizationTenant');
    }

    private isServiceRequestForLoggedInOrg(sr: ServiceRequest | any): boolean {
        const orgId = (this.auth.getOrganizationId() ?? '').toString().trim();
        if (!orgId) return true; // can't scope without org id

        const srOrgId = this.extractTenantOrgIdFromServiceRequest(sr);
        if (!srOrgId) return false; // strict: hide unscoped records in org-wide view
        return srOrgId === orgId;
    }



    // Row enrichment (keys with spaces, best for user-friendly detail view)


    tableFilter = new Map([
        ['status', ['active', 'completed', 'on-hold', 'revoked', 'entered-in-error', 'draft']]
    ]);

    override ngOnInit(): void {
        this.patientId = this.stateService.currentPatientId$.getValue();
        this.useOrgWide = !this.patientId;

        this.subs.add(
            combineLatest([
                this.srSearch.dataSource.connect(),
                this.availabilitySets$
            ]).subscribe(([rows, availabilitySets]) => {
                rows.forEach((row: any) => {
                    const resource = row.rawResource || row.actualResource || row;
                    const availability = this.computeServiceAvailabilityLabel(resource, availabilitySets);
                    row.available = availability;
                    if (row.rawResource) (row.rawResource as any).available = availability;
                    if (row.actualResource) (row.actualResource as any).available = availability;
                    // Also ensure any other top-level resource-like structure has it
                    if (row.resourceType === 'ServiceRequest') (row as any).available = availability;
                });
            })
        );

        // Initialize search service
        this.route.paramMap.pipe(map(params => params.get('type'))).subscribe((type) => {
            this.srSearch.init(type || 'all', this.patientId);
        });
        // const type = (this.route.snapshot.paramMap.get('type') || '')
        //     .toString()
        //     .trim()
        //     .toLowerCase();
        // this.srSearch.init(type, this.patientId);

        // Track data source connect and apply local category filtering to batched results
        // this.subToService = this.srSearch.dataSource.connect().subscribe(rows => {
        //     // const currentTab = this.categoryFiltering.value || 'all';
        //     // this.fullDataSet = this.filterByCategory(rows, currentTab);
        // });

        // const categorySub = this.categoryFiltering.valueChanges.subscribe(val => {
        //     const rows = this.srSearch.dataSource.data;
        //     this.fullDataSet = this.filterByCategory(rows, val || 'all');
        // });

        const refreshSub = this.resourceRefresh.onRefresh('ServiceRequest').subscribe(() => {
            this.srSearch.refresh();
        });

        // assuming we can push these subs into a generic array or just let them live if component lives
        if ((this as any).subscriptionList) {
            // (this as any).subscriptionList.push(categorySub, refreshSub);
        }

        this.allowedServiceRequestTypes$ = this.auth.user.pipe(
            map(() => {
                const allTypes = ['laboratory', 'imaging', 'procedure', 'surgery', 'observation', 'others'];
                return this.auth.getFilteredServiceRequestTypes(allTypes);
            })
        );

        this.computePermissions('serviceRequest');

        this.route.queryParamMap.subscribe(params => {
            const id = params.get('serviceRequestId');
            this.serviceRequestIdFromRoute = id;
            if (!id) {
                this.autoDialogOpened = false;
            }
            this.autoOpenDialogFromRoute();
        });
    }

    private filterByCategory(rows: any[], category: string): any[] {
        if (!rows) return [];
        if (category === 'all') return rows;
        return rows.filter(r => {
            const raw = r.rawResource || r.actualResource || r;
            const type = inferServiceRequestType(raw);
            if (category === 'others') {
                return type !== 'laboratory' && type !== 'imaging' && type !== 'procedure' && type !== 'surgery' && type !== 'observation';
            }
            return type === category;
        });
    }

    onSearchCriteriaChanged(criteria: any): void {
// $#$        // /* alert(JSON.stringify(criteria) */)
        //      const filters: any = {};
        // const field = event.field || this.selectedSearchField;
        // const term = event.term;

        // if (field === 'medication-in-stock') {
        //   // Standard FHIR 'code' search parameter for MedicationRequest handles both
        //   // medicationCodeableConcept and medicationReference.code.

        //   filters['medication.code:text'] = String(term).toLowerCase();
        // } else if (field === 'medication-not-in-stock') {
        //   filters['code:text'] = String(term).toLowerCase();
        // } else {
        //   filters[field] = term;
        // }

        // this.searchService.search(filters);
        // this.paginatorPageIndex = 0;


        const filters: any = {};
        const field = criteria.field || this.selectedSearchField;
        const term = criteria.term;
        if (term && field) {
            filters[field] = term;
        }

        if (criteria?.status && criteria.status.length > 0) {
            filters.status = criteria.status.join(',');
        }
        // add tabs if there is

// $#$        // /* alert(JSON.stringify(filters) */)
        this.srSearch.search(filters);
    }

    onPageChange(event: PageEvent): void {
        this.pageSize = event.pageSize;
        this.paginatorPageIndex = event.pageIndex;
        this.srSearch.changePage(event.pageIndex);
    }
    private matchesServiceRequestType(sr: any, type: string): boolean {
        const t = (type || '').toString().trim().toLowerCase();

        // If no type specified, show everything
        if (!t) return true;

        // Lab uses its own component
        if (t === 'lab-tests') return false;

        const categories: any[] = Array.isArray(sr?.category) ? sr.category : [];
        const toText = (v: any) => (v == null ? '' : String(v)).trim().toLowerCase();

        const codes = categories
            .flatMap((c: any) => (Array.isArray(c?.coding) ? c.coding : []))
            .map((cc: any) => toText(cc?.code))
            .filter(Boolean);

        const displays = categories
            .flatMap((c: any) => [
                toText(c?.text),
                ...(Array.isArray(c?.coding) ? c.coding.map((cc: any) => toText(cc?.display)) : [])
            ])
            .filter(Boolean);

        const hasCategory = (needle: string) => {
            const n = toText(needle);
            return codes.includes(n) || displays.includes(n);
        };

        // Match specific types
        if (t === 'imaging') return hasCategory('imaging');
        if (t === 'procedure') return hasCategory('procedure');
        if (t === 'surgery') return hasCategory('surgery');
        if (t === 'observation') return hasCategory('observation') || hasCategory('observations') || hasCategory('vital') || hasCategory('vitals') || hasCategory('vital-signs');
        if (t === 'laboratory' || t === 'lab') return hasCategory('laboratory') || hasCategory('lab') || hasCategory('lab-tests');

        // Handle "others" - everything except the main buckets + lab
        if (t === 'others') {
            const excluded = new Set(['laboratory', 'imaging', 'procedure', 'surgery', 'observation', 'observations', 'vital', 'vitals', 'vital-signs', 'lab', 'lab-tests']);
            const present = new Set<string>([...codes, ...displays].map(toText).filter(Boolean));

            // If no category recorded, treat as others
            if (!present.size) return true;

            // Only include if it doesn't match any excluded category
            return !Array.from(excluded).some((x) => present.has(x));
        }

        // Fallback: match by category code/text
        return hasCategory(t);
    }
    sRServ = inject(ServiceRequestsService);
    async transformCallbackForAddingInvoice(row: any): Promise<any> {
        if (!row || !row.id || !row.subject?.reference) {
            return row;
        }

        const patientId = row.subject.reference.split("/")[1];
        const cacheKey = `${patientId}-${row.id}`;

        try {
            // Check cache first to avoid duplicate requests
            if (this.invoiceCacheMap.has(cacheKey)) {
                const cached = await this.invoiceCacheMap.get(cacheKey);
                return { ...row, ...cached };
            }

            // Create promise and store in cache
            const promise = this.buildBillingSnapshot(row as ServiceRequest, patientId).then((snapshot) => ({
                relatedChargeItems: snapshot.chargeItem ? [snapshot.chargeItem] : [],
                relatedInvoices: snapshot.invoices,
                hasBalancedInvoice: snapshot.hasBalancedInvoice
            }));

            this.invoiceCacheMap.set(cacheKey, promise);

            const snapshot = await promise;
            const transformed: any = {
                ...row,
                ...snapshot
            };

            return transformed;
        } catch (error) {
            console.error('Error transforming invoice data:', error);
            // Return original row if transformation fails
            return { ...row, relatedChargeItems: [], relatedInvoices: [] };
        }
    }

    // Clear cache when component is destroyed
    override ngOnDestroy(): void {
        this.invoiceCacheMap.clear();
        this.srSearch.destroy();
        super.ngOnDestroy();
    }

    // Open dialog for adding new service requests
    openAdd(): void {
        if (!this.auth.can('serviceRequest', 'add')) {
            this.errorService.openandCloseError('You do not have permission to add service requests.');
            return;
        }

        // Check if user has ANY service request permissions
        if (!this.auth.hasAnyServiceRequestPermission()) {
            this.errorService.openandCloseError('You do not have permission to add any type of service request.');
            return;
        }

        this.dialog.open(AddServiceRequestComponent, {
            maxWidth: '720px',
            maxHeight: '90vh',
            autoFocus: false
        }).afterClosed().subscribe(() => {
            // Refresh explicitly so a newly added request appears without navigation.
            this.srSearch.refresh();
        });
    }


    async showRow(row: any): Promise<MatDialogRef<DetailzViewzComponent>> {
        row.Resource_Identifier = row.id;
        row.systemReference = 'ServiceRequest/' + row.id;
        if (row.note && Array.isArray(row.note)) {
            row.flattenedNote = row.note.map((n: any) => n.text).join(' | ');
        }
        const currentTabType = (this.route.snapshot.paramMap.get('type') || '').toString().trim().toLowerCase();
        const actionButtons = await firstValueFrom(
            this.sRServ.filterVisibleActions$(this.buildServiceRequestActions(row as any), row as any, { currentTabType })
        );
        const dialogRef = this.dialog.open(DetailzViewzComponent, {
            maxHeight: '93vh',
            maxWidth: '90vh',
            data: {
                resourceData: row,
                detailsBuilderObject: this.detailsBuilder,
                excludeKeys: ['text', 'contained', 'meta'],
                actionButtons
            }
        });

        const sub = (dialogRef.componentInstance as any)?.actionInvoked?.subscribe(async ({ key }: any) => {
            await this.handleDetailAction(key, row as ServiceRequest);
            if (key === 'revokeRequest') {
                dialogRef.close();
            }
        });
        dialogRef.afterClosed().subscribe(() => {
            sub?.unsubscribe();
            // Actions such as revoke and procedure creation can update resources
            // while this detail dialog remains open.
            this.srSearch.refresh();
        });

        return dialogRef;
    }

    private async autoOpenDialogFromRoute(): Promise<void> {
        if (!this.serviceRequestIdFromRoute || this.autoDialogOpened) {
            return;
        }

        // Set immediately to prevent race conditions during async operations
        this.autoDialogOpened = true;

        // First try to find in existing data
        let target = this.fullDataSet.length > 0
            ? this.fullDataSet.find((item: any) => item?.actualResource?.id === this.serviceRequestIdFromRoute)
            : null;

        if (target?.actualResource) {
            // Found in cache, proceed normally
            setTimeout(() => {
                this.showRow(target.actualResource);
                void this.router.navigate([], {
                    relativeTo: this.route,
                    queryParams: { ...this.route.snapshot.queryParams, serviceRequestId: null },
                    queryParamsHandling: 'merge',
                    replaceUrl: true
                });
            });
            return;
        }

        // Not found in cache, try to fetch individually
        try {
            const fetchedResource = await this.fetchServiceRequestById(this.serviceRequestIdFromRoute);
            if (fetchedResource) {
                // Apply same transformations as cached data
                const transformed = await this.transformCallbackForAddingInvoice(fetchedResource);

                // Get availability sets (reuse from existing data loading if available, or fetch)
                const availabilitySets = await firstValueFrom(
                    this.utilityService.getAllServiceRequestCodesFromCodeSystem().pipe(
                        map((options: any[]) => {
                            const pairSet = new Set<string>();
                            const codeSet = new Set<string>();
                            for (const opt of (options || [])) {
                                if (!opt?.active) continue;
                                const code = (opt?.code || '').toString().trim().toLowerCase();
                                if (!code) continue;
                                codeSet.add(code);
                                const system = (opt?.system || '').toString().trim().toLowerCase();
                                if (system) pairSet.add(`${system}::${code}`);
                            }
                            return { pairSet, codeSet };
                        }),
                        catchError(() => of({ pairSet: new Set<string>(), codeSet: new Set<string>() }))
                    )
                );

                const transformedWithAvailability = {
                    ...transformed,
                    available: this.computeServiceAvailabilityLabel(transformed as ServiceRequest, availabilitySets)
                };

                // Create target object in same format as cached data
                target = {
                    actualResource: transformedWithAvailability
                };

                this.autoDialogOpened = true;
                setTimeout(async () => {
                    const dialogRef = await this.showRow(target.actualResource);
                    
                    dialogRef.afterClosed().subscribe(() => {
                        this.srSearch.refresh();
                    });

                    void this.router.navigate([], {
                        relativeTo: this.route,
                        queryParams: { ...this.route.snapshot.queryParams, serviceRequestId: null },
                        queryParamsHandling: 'merge',
                        replaceUrl: true
                    });
                });
            }
        } catch (error) {
            console.error('Error in auto-opening service request:', error);
        }
    }

    private computeServiceAvailabilityLabel(
        sr: ServiceRequest | any,
        sets: { pairSet: Set<string>; codeSet: Set<string>; displaySet?: Set<string> }
    ): 'Available' | 'Unavailable' {
        const codings = Array.isArray(sr?.code?.coding) ? sr.code.coding : [];

        for (const coding of codings) {
            const code = (coding?.code || '').toString().trim().toLowerCase();
            const system = (coding?.system || '').toString().trim().toLowerCase();
            const display = (coding?.display || '').toString().trim().toLowerCase();

            if (code) {
                // const key = system ? `${system}::${code}` : '';
                const ok = sets.codeSet.has(code);
                if (ok) return 'Available';
            }
            if (display && sets.displaySet?.has(display)) return 'Available';
        }

        const text = (sr?.code?.text || '').toString().trim().toLowerCase();
        if (text) {
            if (sets.displaySet?.has(text)) return 'Available';
            if (sets.codeSet.has(text)) return 'Available';
        }

        return 'Unavailable';
    }

    private buildServiceRequestActions(row: ServiceRequest & any): DetailActionButton[] {
        if ((row as any)?.available !== 'Available') {
            return [
                {
                    key: 'printRequest',
                    label: 'Print Request',
                    icon: 'print',
                    color: 'primary', visibility: { includedStatus: ['active'] },
                    ignoreCapabilities: true
                },
                // {
                //     key: 'viewResults',
                //     label: 'View Results',
                //     icon: 'visibility',
                //     color: 'primary',
                //     ignoreCapabilities: true,
                //     visibility: { includedStatus: ['active', 'completed'] },
                //     capabilities: [{ resource: 'serviceRequest', action: 'add' }]
                // },
                // {
                //     key: 'editItemsRequired',
                //     label: 'Confirm Items Used',
                //     icon: 'fact_check',
                //     color: 'primary',
                //     ignoreCapabilities: true,
                //     visibility: { includedStatus: ['active'] },
                //     capabilities: [{ resource: 'serviceRequest', action: 'fulfill' }]
                // },
                // {
                //     key: 'editRequest',
                //     label: 'Edit Request',
                //     icon: 'edit',
                //     color: 'primary',
                //     ignoreCapabilities: true,
                //     visibility: { includedStatus: ['draft'] },
                //     capabilities: [{ resource: 'serviceRequest', action: 'update' }]
                // },
                {
                    key: 'revokeRequest',
                    label: 'Revoke Request',
                    icon: 'block',
                    color: 'warn',
                    ignoreCapabilities: true,
                    visibility: { includedStatus: ['active', 'on-hold'] },
                    capabilities: [{ resource: 'serviceRequest', action: 'revoke' }]
                },
            ];
        }
        return [
            {
                key: 'printRequest',
                label: 'Print Request',
                icon: 'print',
                color: 'primary',
                visibility: { includedStatus: ['active'] },
                ignoreCapabilities: true
            },
            {
                key: 'editItemsRequired',
                label: 'Confirm Items Used',
                icon: 'fact_check',
                color: 'primary',
                ignoreCapabilities: true,
                visibility: { includedStatus: ['active'] },
                capabilities: [{ resource: 'serviceRequest', action: 'fulfill' }]
            },
            {
                key: 'addImagingStudy',
                label: 'Add Imaging',
                icon: 'photo_camera',
                color: 'accent',
                ignoreCapabilities: true,
                visibility: { includedStatus: ['active'], includedServiceRequestTypes: ['imaging'] },
                capabilities: [{ resource: 'serviceRequest', action: 'fulfill' }]
            },
            {
                key: 'addImagingObservation',
                label: 'Add Observation',
                icon: 'biotech',
                color: 'accent',
                ignoreCapabilities: true,
                visibility: { includedStatus: ['active'], includedServiceRequestTypes: ['imaging'] },
                capabilities: [{ resource: 'serviceRequest', action: 'fulfill' }]
            },
            {
                key: 'viewResults',
                label: 'View Results',
                icon: 'visibility',
                color: 'primary',
                ignoreCapabilities: true,
                visibility: { includedStatus: ['active', 'completed'] },
                capabilities: [{ resource: 'serviceRequest', action: 'add' }]
            },
            {
                key: 'addObservationResult',
                label: 'Add Result',
                icon: 'biotech',
                color: 'accent',
                ignoreCapabilities: true,
                visibility: { includedStatus: ['active'], includedServiceRequestTypes: ['observation'] },
                capabilities: [{ resource: 'observation', action: 'add' }]
            },
            {
                key: 'completeObservationRequest',
                label: 'Complete Request',
                icon: 'check_circle',
                color: 'primary',
                ignoreCapabilities: true,
                visibility: { includedStatus: ['active'], includedServiceRequestTypes: ['observation'] },
                capabilities: [{ resource: 'serviceRequest', action: 'complete' }]
            },
            {
                key: 'addSpecimenForRequest',
                label: 'Add Specimen',
                icon: 'science',
                color: 'accent',
                ignoreCapabilities: true,
                visibility: { includedStatus: ['active'], includedServiceRequestTypes: ['others'] },
                capabilities: [{ resource: 'specimen', action: 'add' }]
            },
            {
                key: 'addImagingForOthers',
                label: 'Add Imaging',
                icon: 'photo_camera',
                color: 'accent',
                ignoreCapabilities: true,
                visibility: { includedStatus: ['active'], includedServiceRequestTypes: ['others'] },
                capabilities: [{ resource: 'serviceRequest', action: 'fulfill' }]
            },
            {
                key: 'addOthersResult',
                label: 'Add Result',
                icon: 'biotech',
                color: 'accent',
                ignoreCapabilities: true,
                visibility: { includedStatus: ['active'], includedServiceRequestTypes: ['others'] },
                capabilities: [{ resource: 'observation', action: 'add' }]
            },
            {
                key: 'completeOthersRequest',
                label: 'Complete Request',
                icon: 'check_circle',
                color: 'primary',
                ignoreCapabilities: true,
                visibility: { includedStatus: ['active'], includedServiceRequestTypes: ['others'] },
                capabilities: [{ resource: 'serviceRequest', action: 'complete' }]
            },
            {
                key: 'revokeRequest',
                label: 'Revoke Request',
                icon: 'block',
                color: 'warn',
                ignoreCapabilities: true,
                visibility: { includedStatus: ['active', 'on-hold'] },
                capabilities: [{ resource: 'serviceRequest', action: 'revoke' }]
            },
            // {
            //     key: 'addDiagnosticReport',
            //     label: 'Add Diagnostic Report',
            //     icon: 'description',
            //     color: 'accent',
            //     ignoreCapabilities: true,
            //     visibility: { includedStatus: ['active'], includedServiceRequestTypes: ['imaging'] },
            //     capabilities: [{ resource: 'diagnosticReport', action: 'add' }]
            // },
            {
                key: 'addServiceReport',
                label: 'View / Add Report',
                icon: 'description',
                color: 'accent',
                ignoreCapabilities: true,
                visibility: { includedStatus: ['active', 'completed'], includedServiceRequestTypes: ['imaging'] },
                capabilities: [{ resource: 'diagnosticReport', action: 'add' }]
            },
            //see report
            // {
            //     key: 'viewReport',
            //     label: 'View Report',
            //     icon: 'description',
            //     color: 'primary',
            //     ignoreCapabilities: true,
            //     visibility: { includedStatus: ['active', 'completed'], includedServiceRequestTypes: ['imaging', 'procedure', 'surgery'] },
            //     capabilities: [{ resource: 'diagnosticReport', action: 'view' }]



            // },
            {
                key: 'addProcedure',
                label: 'Procedure Report',
                icon: 'healing',
                color: 'accent',
                ignoreCapabilities: true,
                visibility: { includedStatus: ['active', 'completed'], includedServiceRequestTypes: ['procedure'] },
                capabilities: [{ resource: 'serviceRequest', action: 'fulfill' }]
            },
            {
                key: 'addSurgeryProcedure',
                label: 'Surgery Report',
                icon: 'healing',
                color: 'accent',
                ignoreCapabilities: true,
                visibility: { includedStatus: ['active', 'completed'], includedServiceRequestTypes: ['surgery'] },
                capabilities: [{ resource: 'serviceRequest', action: 'fulfill' }]
            },
            {
                key: 'activateRequest',
                label: 'Activate Request',
                icon: 'play_arrow',
                color: 'primary',
                ignoreCapabilities: true,
                visibility: { includedStatus: ['draft'] },
                capabilities: [{ resource: 'serviceRequest', action: 'add' }]
            }
            // ,
            // {
            //     key: 'editRequest',
            //     label: 'Edit Request',
            //     icon: 'edit',
            //     color: 'primary',
            //     ignoreCapabilities: true,
            //     visibility: { includedStatus: ['draft'] },
            //     capabilities: [{ resource: 'serviceRequest', action: 'update' }]
            // }
        ];
    }

    private async handleDetailAction(actionKey: string, row: ServiceRequest & any): Promise<void> {
        if (!actionKey) return;

        switch (actionKey) {
            case 'printRequest':
                this.requestPrint.printServiceRequest(row as ServiceRequest);
                return;

            case 'editItemsRequired': {
                const patientId = row.subject?.reference?.split('/').pop() || this.patientId || '';
                if (!patientId) {
                    this.errorService.openandCloseError('Patient context not found for this ServiceRequest.');
                    return;
                }

                this.dialog.open(ItemsRequiredEditorComponent, {
                    maxWidth: '750px',
                    maxHeight: '90vh',
                    autoFocus: false,
                    disableClose: true,
                    data: {
                        serviceRequest: row as ServiceRequest,
                        patientId
                    }
                });
                return;
            }

            case 'addImagingObservation':
                await this.handleAddImagingObservation(row);
                return;

            case 'viewResults':
                this.viewServiceRequestResults(row as ServiceRequest);
                return;

            case 'addObservationResult':
                await this.handleAddObservationResult(row as ServiceRequest);
                return;

            case 'completeObservationRequest':
                await this.completeObservationServiceRequest(row as ServiceRequest);
                return;

            case 'addSpecimenForRequest':
                this.handleAddSpecimenForRequest(row as ServiceRequest);
                return;

            case 'addImagingForOthers':
                await this.handleAddImaging(row);
                return;

            case 'addOthersResult':
                await this.handleAddObservationResult(row as ServiceRequest);
                return;

            case 'completeOthersRequest':
                await this.completeOthersServiceRequest(row as ServiceRequest);
                return;

            case 'addDiagnosticReport':
                await this.handleAddDiagnosticReport(row);
                return;

            case 'addServiceReport':
                await this.handleAddServiceReport(row);
                return;

            case 'addImagingStudy':
                await this.handleAddImaging(row);
                return;

            case 'addProcedure':
                await this.handleAddProcedure(row);
                return;

            case 'addSurgeryProcedure':
                await this.handleAddProcedure(row);
                return;

            case 'revokeRequest':
                await this.revokeServiceRequest(row);
                return;

            case 'activateRequest':
                this.encounterService.activateServiceRequest(row);
                return;
            case 'editRequest':
                this.errorService.openandCloseError('This action is not wired yet for Service Requests.');
                return;

            default:
                console.warn('Unknown action key:', actionKey);
                return;
        }
    }

    private async handleAddImagingObservation(sr: ServiceRequest & any): Promise<void> {
        const status = (sr?.status || '').toString().toLowerCase();
        if (!['active', 'completed'].includes(status)) {
            this.errorService.openandCloseError('Only ACTIVE or COMPLETED service requests can open a Procedure report.');
            return;
        }

        const patientId = sr.subject?.reference?.split('/').pop() || this.patientId || '';
        if (!patientId) {
            this.errorService.openandCloseError('Patient context not found for this ServiceRequest.');
            return;
        }

        // Observation results still require payment; reuse the same resolver used for ImagingStudy results.
        const billing = await firstValueFrom(this.resolveBillingService.resolveBillingAndPlaceholderForImagingStudy(sr, patientId));
        if (!billing?.hasBalancedInvoice) {
            this.errorService.openandCloseError('Payment has not been made (Invoice must be balanced).');
            return;
        }

        this.dialog.open(RequiredObservationResultsTableComponent, {
            maxWidth: '900px',
            maxHeight: '90vh',
            autoFocus: false,
            data: {
                serviceRequest: sr as ServiceRequest,
                patientId,
                chargeItem: billing.chargeItem ?? null,
                enforcePaymentForFinal: true,
                paymentBalanced: billing.hasBalancedInvoice
            }
        });
    }

    private viewServiceRequestResults(sr: ServiceRequest): void {
        const srId = (sr?.id ?? '').toString().trim();
        if (!srId) {
            this.errorService.openandCloseError('Cannot view results: ServiceRequest is missing an id.');
            return;
        }

        const patientId = sr.subject?.reference?.split('/').pop() || this.patientId || '';
        if (!patientId) {
            this.errorService.openandCloseError('Cannot view results: Patient context not found.');
            return;
        }

        this.dialog.open(RequiredObservationResultsTableComponent, {
            maxWidth: '900px',
            maxHeight: '90vh',
            autoFocus: false,
            data: {
                serviceRequest: sr,
                patientId,
                forViewingOnly: true,
                enforcePaymentForFinal: false,
                paymentBalanced: true
            }
        });
    }

    private async handleAddObservationResult(sr: ServiceRequest): Promise<void> {
        const status = (sr?.status || '').toString().toLowerCase();
        if (status !== 'active') {
            this.errorService.openandCloseError('Only ACTIVE requests can receive results.');
            return;
        }

        const patientId = sr.subject?.reference?.split('/').pop() || this.patientId || '';
        if (!patientId) {
            this.errorService.openandCloseError('Patient context not found for this ServiceRequest.');
            return;
        }

        const billing = await this.buildBillingSnapshot(sr, patientId);
        if (status === 'active' && !billing.hasBalancedInvoice) {
            this.errorService.openandCloseError('Payment has not been made (Invoice must be balanced).');
            return;
        }

        this.dialog.open(RequiredObservationResultsTableComponent, {
            maxWidth: '900px',
            maxHeight: '90vh',
            autoFocus: false,
            data: {
                serviceRequest: sr,
                patientId,
                chargeItem: billing.chargeItem ?? null,
                enforcePaymentForFinal: true,
                paymentBalanced: billing.hasBalancedInvoice
            }
        });
    }

    resolveBillingService = inject(LabBillingPlaceholderService);

    private async buildBillingSnapshot(
        sr: ServiceRequest,
        patientIdOverride?: string | null
    ): Promise<{ patientId: string; chargeItem: ChargeItem | null; invoices: Invoice[]; hasBalancedInvoice: boolean; selectedProcedure?: Procedure | null }> {
        const resolvedPatientId = patientIdOverride
            || sr.subject?.reference?.split('/').pop()
            || this.patientId
            || '';

        if (!resolvedPatientId) {
            return { patientId: '', chargeItem: null, invoices: [], hasBalancedInvoice: false };
        }

        const type = inferServiceRequestType(sr);
        if (type === 'laboratory') {
            const billing = await firstValueFrom(this.resolveBillingService.resolveBillingAndPlaceholderForLab(sr, resolvedPatientId));
            return {
                patientId: resolvedPatientId,
                chargeItem: billing?.chargeItem ?? null,
                invoices: billing?.linkedInvoices ?? [],
                hasBalancedInvoice: !!billing?.hasBalancedInvoice
            };
        }
        if (type === 'imaging') {
            const billing = await firstValueFrom(this.resolveBillingService.resolveBillingAndPlaceholderForImagingStudy(sr, resolvedPatientId));
            return {
                patientId: resolvedPatientId,
                chargeItem: billing?.chargeItem ?? null,
                invoices: billing?.linkedInvoices ?? [],
                hasBalancedInvoice: !!billing?.hasBalancedInvoice
            };
        }
        if (type === 'procedure' || type === 'surgery') {
            const billing = await firstValueFrom(this.resolveBillingService.resolveBillingAndPlaceholderForProcedure(sr, resolvedPatientId));
            return {
                patientId: resolvedPatientId,
                chargeItem: billing?.chargeItem ?? null,
                invoices: billing?.linkedInvoices ?? [],
                hasBalancedInvoice: !!billing?.hasBalancedInvoice,
                selectedProcedure: billing?.selectedProcedure ?? null
            };
        }

        const fallback = await firstValueFrom(
            this.serviceRequestPaymentResolver.resolvePaymentForServiceRequest(sr as ServiceRequest, resolvedPatientId)
        );
        return {
            patientId: resolvedPatientId,
            chargeItem: (fallback?.chargeItems || [])[0] ?? null,
            invoices: fallback?.invoices ?? [],
            hasBalancedInvoice: !!fallback?.hasBalancedInvoice
        };
    }

    private async resolveBillingSnapshotForRevocation(
        sr: ServiceRequest,
        patientId: string
    ): Promise<{ hasBalancedInvoice: boolean; invoices: Invoice[] }> {
        const snapshot = await this.buildBillingSnapshot(sr, patientId);
        return { hasBalancedInvoice: snapshot.hasBalancedInvoice, invoices: snapshot.invoices };
    }

    private async handleAddDiagnosticReport(sr: ServiceRequest & any): Promise<void> {
        const status = (sr?.status || '').toString().toLowerCase();
        if (status !== 'active') {
            this.errorService.openandCloseError('Only ACTIVE service requests can be fulfilled.');
            return;
        }
        const billing = await this.buildBillingSnapshot(sr);
        const patientId = billing.patientId || sr.subject?.reference?.split('/').pop() || this.patientId || '';
        if (!patientId) {
            this.errorService.openandCloseError('Patient context not found for this ServiceRequest.');
            return;
        }

        if (!billing.hasBalancedInvoice) {
            this.errorService.openandCloseError('Payment has not been made (Invoice must be balanced).');
            return;
        }

        this.dialog.open(AddReportComponent as any, {
   maxWidth: '95vw',
      
      width: '650px',
            maxHeight: '95vh',
            autoFocus: false,
            data: {
                serviceRequest: sr,
                patientId
            },
            disableClose: true,
        });
    }

    private async handleAddServiceReport(sr: ServiceRequest & any): Promise<void> {
        const status = (sr?.status || '').toString().toLowerCase();
        if (status !== 'active' && status !== 'completed') {
            this.errorService.openandCloseError('Only ACTIVE service requests can be fulfilled.');
            return;
        }

        const snapshot = await this.buildBillingSnapshot(sr);
        const patientId = snapshot.patientId || sr.subject?.reference?.split('/').pop() || this.patientId || '';
        if (!patientId) {
            this.errorService.openandCloseError('Patient context not found for this ServiceRequest.');
            return;
        }

        if (!snapshot.hasBalancedInvoice) {
            this.errorService.openandCloseError('Payment has not been made (Invoice must be balanced).');
            return;
        }

        // Open AddReportComponent the same way group reports are opened in LabRequests.
        this.dialog.open(AddReportComponent as any, {
            maxWidth: '95vw',
      
      width: '650px',
            maxHeight: '90vh',
            autoFocus: false,
            disableClose: true,
            data: {
                serviceRequests: [sr as ServiceRequest],
                serviceRequestIds: [(sr?.id ?? '').toString()].filter(Boolean),
                patientId,
                requisitionId: (sr as any)?.requisitionId ?? null
            }
        });
    }

    private async handleAddImaging(sr: ServiceRequest & any): Promise<void> {
        const status = (sr?.status || '').toString().toLowerCase();
        if (status !== 'active') {
            this.errorService.openandCloseError('Only ACTIVE service requests can be fulfilled.');
            return;
        }

        const patientId = sr.subject?.reference?.split('/').pop() || this.patientId || '';
        const billing = await firstValueFrom(this.resolveBillingService.resolveBillingAndPlaceholderForImagingStudy(sr, patientId));

        if (!billing.hasBalancedInvoice) {
            this.errorService.openandCloseError('Payment has not been made (Invoice must be balanced).');
            return;
        }
        if (!sr?.id) {
            this.errorService.openandCloseError('ServiceRequest ID missing.');
            return;
        }

        const dialogRef = this.dialog.open(AddImagingStudyComponent, {
            maxWidth: '900px',
            maxHeight: '95vh',
            autoFocus: false,
            disableClose: true,
            data: {
                serviceRequest: sr as ServiceRequest,
                patientId,
                existingImagingStudy: billing.selectedImagingStudy
            }
        });

        dialogRef.afterClosed().subscribe(async (saved: ImagingStudy | null | undefined) => {
            if (!saved?.id) return;
            try {
                if (billing.chargeItem?.id) {
                    await this.updateChargeItemServiceRefs(billing.chargeItem, 'ImagingStudy', saved.id);
                }
                this.infoDialogService.info('Imaging saved.');
            } catch (e) {
                console.error(e);
                this.errorService.openandCloseError('Imaging saved, but failed to link billing ChargeItem.');
            }
        });
    }

    private async handleAddProcedure(sr: ServiceRequest & any): Promise<void> {
        const status = (sr?.status || '').toString().toLowerCase();
        const billing = await this.buildBillingSnapshot(sr);
        if (!billing.patientId) {
            this.errorService.openandCloseError('Patient context not found for this ServiceRequest.');
            return;
        }

        const existingProcedure = billing.selectedProcedure ?? null;
        const isFinalizedProcedure = (existingProcedure?.status ?? '').toString().toLowerCase() === 'completed';

        // A completed request should open its finalized Procedure report for
        // viewing, rather than failing the active-request check or showing a
        // blank form.
        if (isFinalizedProcedure) {
            this.dialog.open(ProcedureReportViewDialogComponent, {
                maxWidth: '760px',
                maxHeight: '92vh',
                autoFocus: false,
                data: {
                    serviceRequest: sr as ServiceRequest,
                    patientId: billing.patientId,
                    existingProcedure,
                    readOnly: true,
                }
            }).afterClosed().subscribe(() => this.srSearch.refresh());
            return;
        }

        if (status !== 'active') {
            this.errorService.openandCloseError('No finalized Procedure report is available for this request.');
            return;
        }

        if (!billing.hasBalancedInvoice) {
            this.errorService.openandCloseError('Payment has not been made (Invoice must be balanced).');
            return;
        }

        const srRef = sr?.id ? `ServiceRequest/${sr.id}` : '';
        if (!srRef) {
            this.errorService.openandCloseError('ServiceRequest ID missing.');
            return;
        }

        const dialogRef = this.dialog.open(ProcedureReportDialogComponent, {
            maxWidth: '760px',
            maxHeight: '92vh',
            autoFocus: false,
            disableClose: true,
            data: {
                serviceRequest: sr as ServiceRequest,
                patientId: billing.patientId,
                existingProcedure
            }
        });

        dialogRef.afterClosed().subscribe(async (proc: Procedure | null | undefined) => {
            if (!proc?.id) return;

            try {
                if (billing.chargeItem?.id) {
                    await this.updateChargeItemServiceRefs(billing.chargeItem, 'Procedure', proc.id);
                }

                this.infoDialogService.info(
                    proc.status === 'completed'
                        ? 'Procedure finalized and request completed.'
                        : 'Procedure draft saved.'
                );
                this.srSearch.refresh();
            } catch (e) {
                console.error(e);
                this.errorService.openandCloseError('Procedure saved, but failed to link billing ChargeItem.');
            }
        });
    }

    private async completeObservationServiceRequest(sr: ServiceRequest): Promise<void> {
        if (inferServiceRequestType(sr) !== 'observation') {
            this.errorService.openandCloseError('Only observation service requests can be completed from this action.');
            return;
        }

        const status = (sr?.status ?? '').toString().toLowerCase();
        if (status !== 'active') {
            this.errorService.openandCloseError('Only ACTIVE observation requests can be completed.');
            return;
        }

        const patientId = sr.subject?.reference?.split('/').pop() || this.patientId || '';
        if (!patientId || !sr?.id) {
            this.errorService.openandCloseError('Patient or ServiceRequest context is missing.');
            return;
        }

        try {
            const readiness = await firstValueFrom(this.resolveObservationRequestReadiness$(sr, patientId));
            if (!readiness.complete) {
                this.errorService.openandCloseError(`Cannot complete request. Missing: ${readiness.missingDisplays.join(', ')}`);
                return;
            }

            await this.finalizeServiceRequestCompletion(sr, patientId);
            this.infoDialogService.info('Observation request completed.');
            this.srSearch.refresh();
        } catch (error) {
            console.error(error);
            this.errorService.openandCloseError('Failed to complete observation request.');
        }
    }

    private async finalizeServiceRequestCompletion(sr: ServiceRequest, patientId: string): Promise<void> {
        const srId = (sr?.id ?? '').toString().trim();
        if (!srId) throw new Error('ServiceRequest id missing');

        // Same finalization pipeline as Procedure: ActivityDefinition snapshot,
        // FIFO inventory consumption, invoice cost snapshots, low-stock alerts —
        // all persisted in one transaction bundle.
        const finalization = await this.finalizationInventory.prepare(srId);
        const inventoryExceptions = finalization.inventoryReconciliations.filter((item) => item.shortageQuantity > 0);
        const lowStockNotifications: Array<{ bundleIndex: number; notification: any }> = [];

        const entries: any[] = [];
        entries.push({
            resource: finalization.serviceRequest,
            request: { method: 'PUT', url: `ServiceRequest/${srId}` }
        });
        for (const delivery of finalization.supplyDeliveries) {
            if (!delivery?.id) continue;
            entries.push({ resource: delivery, request: { method: 'PUT', url: `SupplyDelivery/${delivery.id}` } });
        }
        for (const invoice of finalization.invoices) {
            if (!invoice?.id) continue;
            entries.push({ resource: invoice, request: { method: 'PUT', url: `Invoice/${invoice.id}` } });
        }
        for (const alert of finalization.lowStockAlerts) {
            lowStockNotifications.push({ bundleIndex: entries.length, notification: alert.notification });
            entries.push({ resource: alert.resource, request: { method: 'POST', url: 'CommunicationRequest' } });
        }

        const bundle: Bundle = { resourceType: 'Bundle', type: 'transaction', entry: entries };
        const savedBundle = await firstValueFrom(
            this.http.post<Bundle>(`${this.backendUrl}`, bundle, { headers: this.fhirHeaders() as any })
        );

        const currentPatientId = this.stateService.currentPatientId$.getValue() || '';
        if (currentPatientId && currentPatientId === patientId) {
            this.stateService.processBundleTransaction(savedBundle as any);
        } else {
            this.stateService.processOrgWideBundleTransaction(savedBundle as any);
        }

        for (const alert of lowStockNotifications) {
            const location = savedBundle.entry?.[alert.bundleIndex]?.response?.location || '';
            const communicationId = location.split('/')[1];
            this.unifiedNotificationService.notifyLowStockAlert({ ...alert.notification, ...(communicationId ? { communicationId } : {}) });
        }

        if (inventoryExceptions.length) {
            const details = inventoryExceptions.map((item) => {
                const unit = item.unit ? ` ${item.unit}` : '';
                if (!item.sourceBatchId) {
                    return `${item.itemName}: ${item.requiredQuantity}${unit} required; no matching SupplyDelivery was found, so ${item.shortageQuantity}${unit} was not costed.`;
                }
                return `${item.itemName}: ${item.requiredQuantity}${unit} required; ${item.recordedQuantityUsed}${unit} deducted and ${item.shortageQuantity}${unit} estimated at the latest batch cost.`;
            }).join(' ');
            this.notifications.warning(`Request finalized with inventory exceptions. ${details} Verify Supply Stock and reconcile the delivery record.`, 12000);
        }
    }

    private handleAddSpecimenForRequest(sr: ServiceRequest): void {
        const status = (sr?.status ?? '').toString().toLowerCase();
        if (status !== 'active') {
            this.errorService.openandCloseError('Only ACTIVE requests can receive specimens.');
            return;
        }

        const patientId = sr.subject?.reference?.split('/').pop() || this.patientId || '';
        if (!patientId || !sr?.id) {
            this.errorService.openandCloseError('Patient or ServiceRequest context is missing.');
            return;
        }

        this.dialog.open(AddSpecimenComponent, {
            maxWidth: '900px',
            maxHeight: '90vh',
            autoFocus: false,
            data: { patientId, serviceRequestId: sr.id }
        }).afterClosed().subscribe(() => this.srSearch.refresh());
    }

    private async completeOthersServiceRequest(sr: ServiceRequest): Promise<void> {
        const status = (sr?.status ?? '').toString().toLowerCase();
        if (status !== 'active') {
            this.errorService.openandCloseError('Only ACTIVE requests can be completed.');
            return;
        }

        const patientId = sr.subject?.reference?.split('/').pop() || this.patientId || '';
        if (!patientId || !sr?.id) {
            this.errorService.openandCloseError('Patient or ServiceRequest context is missing.');
            return;
        }

        const choice = await firstValueFrom(
            this.dialog.open(CompleteRequestChoiceDialogComponent, {
                maxWidth: '520px',
                autoFocus: false,
                disableClose: true
            }).afterClosed()
        ) as CompleteRequestChoice | null;

        if (!choice) return;

        if (choice === 'procedureReport') {
            await this.handleAddProcedure(sr);
            return;
        }

        const reportResultsMode: Partial<Record<CompleteRequestChoice, 'Observation' | 'ImagingStudy'>> = {
            imagingReport: 'ImagingStudy',
            labReport: 'Observation',
            genericReport: 'Observation'
        };
        const resultsMode = reportResultsMode[choice];

        if (resultsMode) {
            const billing = await this.buildBillingSnapshot(sr, patientId);
            if (!billing.hasBalancedInvoice) {
                this.errorService.openandCloseError('Payment has not been made (Invoice must be balanced).');
                return;
            }

            const dialogRef = this.dialog.open(AddReportComponent as any, {
                maxWidth: '95vw',
                width: '650px',
                maxHeight: '95vh',
                autoFocus: false,
                disableClose: true,
                data: {
                    serviceRequest: sr,
                    patientId,
                    resultsMode
                }
            });

            // AddReport's own finalize flow completes the ServiceRequest (final
            // report + SR completion + inventory consumption in one bundle).
            // Draft/cancel closes leave the request active so the user can
            // return via Complete Request and finalize later.
            dialogRef.afterClosed().subscribe(() => this.srSearch.refresh());
            return;
        }

        try {
            const readiness = await firstValueFrom(this.resolveObservationRequestReadiness$(sr, patientId));
            if (!readiness.complete) {
                this.errorService.openandCloseError(`Cannot complete request. Missing: ${readiness.missingDisplays.join(', ')}`);
                return;
            }

            await this.finalizeServiceRequestCompletion(sr, patientId);
            this.infoDialogService.info('Request completed.');
            this.srSearch.refresh();
        } catch (error) {
            console.error(error);
            this.errorService.openandCloseError('Failed to complete the request.');
        }
    }

    private async revokeServiceRequest(row: ServiceRequest & any): Promise<void> {
        const srId = (row?.id ?? '').toString().trim();
        if (!srId) {
            this.errorService.openandCloseError('This request has no id, so it cannot be revoked.');
            return;
        }

        const status = (row?.status ?? '').toString().toLowerCase();
        if (status === 'revoked') {
            this.snackBar.open('This service request is already revoked.', 'Dismiss', { duration: 4500 });
            return;
        }
        if (!['active', 'on-hold'].includes(status)) {
            this.errorService.openandCloseError('Only active or on-hold requests can be revoked. Completed requests require an audited reversal workflow.');
            return;
        }

        const patientId = row.subject?.reference?.split('/').pop() || this.patientId || '';
        if (!patientId) {
            this.errorService.openandCloseError('Patient context not found for this ServiceRequest.');
            return;
        }

        let paymentResult: { invoices?: Invoice[]; hasBalancedInvoice?: boolean } | null = null;
        try {
            paymentResult = await this.resolveBillingSnapshotForRevocation(row as ServiceRequest, patientId);
        } catch (error) {
            console.error('Failed to resolve billing for ServiceRequest', srId, error);
            this.errorService.openandCloseError('Unable to resolve billing for this request.');
            return;
        }

        const currentPatientId = this.stateService.currentPatientId$.getValue() || '';
        const persistMode: 'auto' | 'patient' | 'org' = currentPatientId && currentPatientId === patientId
            ? 'patient'
            : (this.useOrgWide ? 'org' : 'patient');

        try {
            await firstValueFrom(this.statusChanges.changeResourceStatus('ServiceRequest', srId, 'revoked', persistMode));
            // Do not wait for the state cache to re-emit before updating the tab.
            this.srSearch.refresh();
        } catch (error) {
            console.error('Failed to revoke ServiceRequest', srId, error);
            this.errorService.openandCloseError('Failed to revoke this service request.');
            return;
        }

        if (paymentResult?.hasBalancedInvoice) {
            this.snackBar.open('Service request revoked. Payment already made, invoice unchanged.', 'Dismiss', { duration: 4500 });
            return;
        }

        const fallbackInvoices: Invoice[] = Array.isArray((row as any)?.relatedInvoices)
            ? (((row as any).relatedInvoices as any[]).filter(Boolean) as Invoice[])
            : [];
        const invoices = (paymentResult?.invoices?.length ? paymentResult.invoices : fallbackInvoices) ?? [];

        const idsToCancel = Array.from(new Set(
            (invoices || [])
                .filter((inv: any) => {
                    const st = (inv?.status ?? '').toString().toLowerCase();
                    return st && st !== 'balanced' && st !== 'cancelled' && st !== 'entered-in-error';
                })
                .map((inv: any) => (inv?.id ?? '').toString().trim())
                .filter(Boolean)
        ));

        if (!idsToCancel.length) {
            this.snackBar.open('Service request revoked. No related unpaid invoice found to cancel.', 'Dismiss', { duration: 4500 });
            return;
        }

        let cancelledInvoices = 0;
        await Promise.all(idsToCancel.map(async (invoiceId) => {
            try {
                await firstValueFrom(this.statusChanges.changeResourceStatus('Invoice', invoiceId, 'cancelled', persistMode));
                cancelledInvoices += 1;
            } catch (error) {
                console.error('Failed to cancel Invoice', invoiceId, error);
            }
        }));

        if (cancelledInvoices > 0) {
            this.snackBar.open(`Service request revoked. Cancelled ${cancelledInvoices} unpaid invoice(s).`, 'Dismiss', { duration: 4500 });
            return;
        }

        this.snackBar.open('Service request revoked. Unable to cancel related invoices.', 'Dismiss', { duration: 4500 });
    }

    private async fetchServiceRequestById(id: string | null): Promise<ServiceRequest | null> {
        if (!id || !id.trim()) {
            return null;
        }

        try {
            const url = `${this.backendUrl}/ServiceRequest/${encodeURIComponent(id.trim())}`;
            const resource = await firstValueFrom(
                this.http.get<ServiceRequest>(url).pipe(
                    catchError((error) => {
                        console.warn(`ServiceRequest/${id} not found on backend:`, error);
                        return of(null);
                    })
                )
            );
            return resource;
        } catch (error) {
            console.error('Error fetching ServiceRequest by ID:', error);
            return null;
        }
    }

    private fhirHeaders() {
        return {
            'Content-Type': 'application/fhir+json',
            Accept: 'application/fhir+json',
            Prefer: 'return=representation'
        } as Record<string, string>;
    }

    private async upsertImagingStudyForServiceRequest(srRef: string, patientId: string, sr: ServiceRequest): Promise<ImagingStudy> {
        const searchUrl = `${this.backendUrl}/ImagingStudy?subject=${encodeURIComponent(`Patient/${patientId}`)}&basedon=${encodeURIComponent(srRef)}&_count=50`;
        const bundle = await firstValueFrom(
            this.http.get<Bundle<ImagingStudy>>(searchUrl).pipe(
                tap((b: any) => {
                    const currentPatientId = this.stateService.currentPatientId$.getValue();
                    const isCurrentPatient = !!currentPatientId && currentPatientId === patientId;
                    if (isCurrentPatient) {
                        this.stateService.processBundleTransaction(b as any);
                    } else {
                        this.stateService.processOrgWideBundleTransaction(b as any);
                    }
                }),
                map((b: any) => b as Bundle<ImagingStudy>)
            )
        );

        const existing = (bundle?.entry || []).map((e: any) => e?.resource).filter(Boolean)[0] as ImagingStudy | undefined;

        const display =
            (sr as any)?.code?.text ||
            (sr as any)?.code?.coding?.[0]?.display ||
            (sr as any)?.code?.coding?.[0]?.code ||
            'Imaging';

        if (existing?.id) {
            const updated: ImagingStudy = {
                ...existing,
                status: 'available',
                description: (existing as any).description || display,
                started: (existing as any).started || new Date().toISOString()
            } as any;

            const saved = await firstValueFrom(
                this.http.put<ImagingStudy>(`${this.backendUrl}/ImagingStudy/${existing.id}`, updated as any, { headers: this.fhirHeaders() as any })
            );
            this.stateService.persistLocalResource(saved as any, 'saved');
            return saved;
        }

        const created: Partial<ImagingStudy> = {
            resourceType: 'ImagingStudy',
            status: 'available',
            subject: { reference: `Patient/${patientId}` } as any,
            basedOn: [{ reference: srRef } as any],
            description: display,
            started: new Date().toISOString()
        };

        const saved = await firstValueFrom(
            this.http.post<ImagingStudy>(`${this.backendUrl}/ImagingStudy`, created as any, { headers: this.fhirHeaders() as any })
        );
        this.stateService.persistLocalResource(saved as any, 'saved');
        return saved;
    }

    private async upsertProcedureForServiceRequest(srRef: string, patientId: string, sr: ServiceRequest): Promise<Procedure> {
        const searchUrl = `${this.backendUrl}/Procedure?subject=${encodeURIComponent(`Patient/${patientId}`)}&based-on=${encodeURIComponent(srRef)}&_count=50`;
        const bundle = await firstValueFrom(
            this.http.get<Bundle<Procedure>>(searchUrl).pipe(
                tap((b: any) => {
                    const currentPatientId = this.stateService.currentPatientId$.getValue();
                    const isCurrentPatient = !!currentPatientId && currentPatientId === patientId;
                    if (isCurrentPatient) {
                        this.stateService.processBundleTransaction(b as any);
                    } else {
                        this.stateService.processOrgWideBundleTransaction(b as any);
                    }
                }),
                map((b: any) => b as Bundle<Procedure>)
            )
        );

        const existing = (bundle?.entry || []).map((e: any) => e?.resource).filter(Boolean)[0] as Procedure | undefined;

        const code = (sr as any)?.code || undefined;

        if (existing?.id) {
            const updated: Procedure = {
                ...existing,
                status: 'completed',
                code: (existing as any).code || code,
                performedDateTime: (existing as any).performedDateTime || new Date().toISOString()
            } as any;

            const saved = await firstValueFrom(
                this.http.put<Procedure>(`${this.backendUrl}/Procedure/${existing.id}`, updated as any, { headers: this.fhirHeaders() as any })
            );
            this.stateService.persistLocalResource(saved as any, 'saved');
            return saved;
        }

        const created: Partial<Procedure> = {
            resourceType: 'Procedure',
            status: 'completed',
            subject: { reference: `Patient/${patientId}` } as any,
            basedOn: [{ reference: srRef } as any],
            code: code as any,
            performedDateTime: new Date().toISOString()
        };

        const saved = await firstValueFrom(
            this.http.post<Procedure>(`${this.backendUrl}/Procedure`, created as any, { headers: this.fhirHeaders() as any })
        );
        this.stateService.persistLocalResource(saved as any, 'saved');
        return saved;
    }

    private resolveObservationRequestReadiness$(
        sr: ServiceRequest,
        patientId?: string
    ): Observable<{ complete: boolean; missingDisplays: string[] }> {
        const srId = (sr?.id ?? '').toString().trim();
        if (!srId) return of({ complete: false, missingDisplays: ['ServiceRequest id'] });

        const srCode = this.getServiceRequestCode(sr);
        const requiredCodes$ = srCode
            ? this.encounterService.getConfiguredRequiredObservationCodes$(srCode).pipe(catchError(() => of([] as CodeableConcept[])))
            : of([] as CodeableConcept[]);

        return combineLatest({
            requiredCodes: requiredCodes$,
            observations: this.listObservationsForServiceRequest$(sr, patientId)
        }).pipe(
            map(({ requiredCodes, observations }) => {
                const finals = (observations || []).filter((o: any) => (o?.status ?? '').toString().toLowerCase() === 'final');
                const configured = Array.isArray(requiredCodes) ? requiredCodes : [];

                if (!configured.length) {
                    return {
                        complete: finals.length > 0,
                        missingDisplays: finals.length > 0 ? [] : ['at least one final observation result']
                    };
                }

                const missing = configured.filter((cc: any) => !finals.some((o: any) => this.codesMatch(o?.code, cc)));
                return {
                    complete: missing.length === 0,
                    missingDisplays: missing.map((cc: any) => this.displayCodeableConcept(cc))
                };
            })
        );
    }

    private listObservationsForServiceRequest$(sr: ServiceRequest, patientId?: string): Observable<Observation[]> {
        const srId = (sr?.id ?? '').toString().trim();
        if (!srId) return of([] as Observation[]);

        const srRef = `ServiceRequest/${srId}`;
        const absoluteSrRef = `${this.backendUrl}/ServiceRequest/${srId}`;
        const patientRef = patientId ? `Patient/${patientId}` : null;

        return this.utilityService.threeTierQueryAndSyncToStateOnce$<Observation>({
            resourceType: 'Observation',
            scope: patientId ? 'patient' : 'orgWide',
            patientId: patientId || undefined,
            patientScopeSearchParam: 'subject',
            urlSearchObject: {
                'based-on': srRef,
                ...(patientRef ? { subject: patientRef } : {}),
                _count: 200
            } as any,
            querySearchObject: [
                { key: 'basedOn.reference', value: [srRef, absoluteSrRef] },
                ...(patientId ? [{ key: 'subject.reference', value: [patientRef, patientId, `${this.backendUrl}/Patient/${patientId}`] }] : [])
            ],
            tier1FollowNextLinks: true,
            tier3TriggerWhenBelowExpectedMinOnTier1Failure: false
        }).pipe(
            map((list) => (list || []).filter((o: any) => {
                const refs = Array.isArray(o?.basedOn) ? o.basedOn : [];
                return refs.some((r: any) => {
                    const ref = (r?.reference ?? '').toString();
                    return ref === srRef || ref === absoluteSrRef || ref.includes(`ServiceRequest/${srId}`);
                });
            })),
            catchError(() => of([] as Observation[]))
        );
    }

    private getServiceRequestCode(sr: ServiceRequest): string | null {
        const codings = Array.isArray(sr?.code?.coding) ? sr.code.coding : [];
        const coding = codings[0];
        if (coding?.code) return String(coding.code);
        return null;
    }

    private displayCodeableConcept(cc: any): string {
        return (cc?.text || cc?.coding?.[0]?.display || cc?.coding?.[0]?.code || 'Unnamed observation').toString();
    }

    private normalizeCodeTokens(cc: any): Set<string> {
        const tokens = new Set<string>();
        const coding = Array.isArray(cc?.coding) ? cc.coding : [];
        for (const c of coding) {
            const code = (c?.code || '').toString().trim();
            if (!code) continue;
            const system = (c?.system || '').toString().trim();
            tokens.add(`${system}|${code}`);
            tokens.add(`|${code}`);
        }
        const text = (cc?.text || '').toString().trim().toLowerCase();
        if (text) tokens.add(`text|${text}`);
        return tokens;
    }

    private codesMatch(a: any, b: any): boolean {
        if (!a || !b) return false;
        const at = this.normalizeCodeTokens(a);
        const bt = this.normalizeCodeTokens(b);
        for (const t of at) {
            if (bt.has(t)) return true;
        }
        return false;
    }

    private async updateChargeItemServiceRefs(chargeItem: ChargeItem, resourceType: 'ImagingStudy' | 'Procedure', newId: string): Promise<void> {
        if (!chargeItem?.id) return;
        const newRef = `${resourceType}/${newId}`;

        const existingServices: Array<{ reference?: string; display?: string }> = Array.isArray((chargeItem as any)?.service)
            ? ((chargeItem as any).service as any[])
            : [];

        const filtered = existingServices
            .filter((s) => {
                const ref = (s?.reference || '').toString();
                if (!ref) return false;
                // Replace any previous refs for same resource type
                if (ref.startsWith(`${resourceType}/`)) return false;
                // Avoid duplicates of newRef
                if (referenceMatches(ref, newRef)) return false;
                return true;
            })
            .concat([{ reference: newRef }]);

        const updated: ChargeItem = {
            ...(chargeItem as any),
            service: filtered as any
        } as any;

        const saved = await firstValueFrom(
            this.http.put<ChargeItem>(`${this.backendUrl}/ChargeItem/${chargeItem.id}`, updated as any, { headers: this.fhirHeaders() as any })
        );

        // Reflect into local state (patient/encounter as appropriate)
        this.stateService.persistLocalResource(saved as any, 'saved');
    }
    statusMappedToPossibleActions: Record<string, DetailActionButton[]> = {
        "active": [
            {
                key: 'fulfillRequest',
                label: 'Fulfill Request',
                icon: 'check_circle',

            }
        ],
        "draft": [

            //activate, edit
            {
                key: 'activateRequest',
                label: 'Activate Request',
                icon: 'play_arrow',
                color: 'primary'
            },
            // {
            //     key: 'editRequest',
            //     label: 'Edit Request',
            //     icon: 'edit',
            //     color: 'primary'
            // }


        ]    //other statuses have no actions
    }


}
